import React, { useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Star, Loader2, CheckCircle, Truck } from 'lucide-react';

// Star rating component
const StarRating = ({ rating, setRating, readonly = false, size = 'lg' }) => {
  const [hoverRating, setHoverRating] = useState(0);
  
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-8 w-8',
    xl: 'h-10 w-10'
  };
  
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => {
        const isFilled = star <= (hoverRating || rating);
        return (
          <button
            key={star}
            type="button"
            disabled={readonly}
            onClick={() => !readonly && setRating(star)}
            onMouseEnter={() => !readonly && setHoverRating(star)}
            onMouseLeave={() => !readonly && setHoverRating(0)}
            className={`transition-all ${readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110'}`}
            data-testid={`star-${star}`}
          >
            <Star
              className={`${sizeClasses[size]} transition-colors ${
                isFilled 
                  ? 'text-amber-400 fill-amber-400' 
                  : 'text-slate-600'
              }`}
            />
          </button>
        );
      })}
    </div>
  );
};

// Review submission dialog
export function ReviewDialog({ open, onOpenChange, request, onReviewSubmitted }) {
  const { api } = useAuth();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/roadside/reviews', {
        request_id: request.id,
        rating,
        comment: comment.trim() || null
      });
      setSubmitted(true);
      toast.success('Thank you for your review!');
      if (onReviewSubmitted) {
        onReviewSubmitted();
      }
    } catch (error) {
      if (error.response?.data?.detail === 'Already reviewed') {
        toast.info('You have already reviewed this service');
        setSubmitted(true);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to submit review');
      }
    }
    setSubmitting(false);
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset state after animation
    setTimeout(() => {
      if (!submitted) {
        setRating(0);
        setComment('');
      }
    }, 300);
  };

  if (submitted) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <div className="text-center py-6">
            <div className="w-16 h-16 rounded-full bg-green-500/10 border border-green-500/30 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-400" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Review Submitted!</h3>
            <p className="text-slate-400 mb-4">
              Thank you for taking the time to rate your experience.
            </p>
            <div className="flex justify-center mb-4">
              <StarRating rating={rating} readonly size="md" />
            </div>
            <Button onClick={handleClose}>Done</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="review-dialog">
        <DialogHeader>
          <DialogTitle>Rate Your Experience</DialogTitle>
          <DialogDescription>
            How was your service with {request?.provider_name || 'your provider'}?
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Provider Info */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-800/50">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <Truck className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <p className="font-medium">{request?.provider_name}</p>
              <p className="text-sm text-slate-400">{request?.service_name}</p>
            </div>
          </div>
          
          {/* Star Rating */}
          <div className="text-center">
            <p className="text-sm text-slate-400 mb-3">Tap to rate</p>
            <div className="flex justify-center">
              <StarRating rating={rating} setRating={setRating} size="xl" />
            </div>
            {rating > 0 && (
              <p className="text-sm mt-2 text-slate-300">
                {rating === 1 && 'Poor'}
                {rating === 2 && 'Fair'}
                {rating === 3 && 'Good'}
                {rating === 4 && 'Very Good'}
                {rating === 5 && 'Excellent'}
              </p>
            )}
          </div>
          
          {/* Comment */}
          <div>
            <label className="text-sm text-slate-400 mb-2 block">
              Share your experience (optional)
            </label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Tell others about your experience..."
              className="h-24 resize-none"
              data-testid="review-comment"
            />
          </div>
        </div>
        
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={handleClose}>
            Skip
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={rating === 0 || submitting}
            className="bg-blue-600 hover:bg-blue-500"
            data-testid="submit-review-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Submitting...
              </>
            ) : (
              'Submit Review'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Review card for displaying reviews
export function ReviewCard({ review, showProvider = false }) {
  return (
    <Card className="bg-slate-800/50" data-testid={`review-card-${review.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <p className="font-medium">{review.reviewer_name}</p>
            <p className="text-xs text-slate-400">
              {new Date(review.created_at).toLocaleDateString()}
            </p>
          </div>
          <StarRating rating={review.rating} readonly size="sm" />
        </div>
        {review.comment && (
          <p className="text-sm text-slate-300 mt-2">{review.comment}</p>
        )}
        {showProvider && review.service_type && (
          <p className="text-xs text-slate-500 mt-2">
            Service: {review.service_type}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// Reviews list component for provider profile
export function ReviewsList({ providerId, limit = 5 }) {
  const { api } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ average: 0, total: 0 });

  React.useEffect(() => {
    fetchReviews();
  }, [providerId]);

  const fetchReviews = async () => {
    try {
      const res = await api.get(`/roadside/providers/${providerId}/reviews`, {
        params: { limit }
      });
      setReviews(res.data || []);
      
      // Calculate stats
      if (res.data?.length > 0) {
        const total = res.data.length;
        const sum = res.data.reduce((acc, r) => acc + r.rating, 0);
        setStats({ average: (sum / total).toFixed(1), total });
      }
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center p-4">
        <Loader2 className="h-6 w-6 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="reviews-list">
      {/* Stats Header */}
      {stats.total > 0 && (
        <div className="flex items-center gap-4 p-4 rounded-lg bg-slate-800/50">
          <div className="text-center">
            <p className="text-3xl font-bold text-amber-400">{stats.average}</p>
            <StarRating rating={Math.round(stats.average)} readonly size="sm" />
          </div>
          <div className="text-sm text-slate-400">
            Based on {stats.total} review{stats.total !== 1 ? 's' : ''}
          </div>
        </div>
      )}
      
      {/* Reviews */}
      {reviews.length === 0 ? (
        <div className="text-center p-6 text-slate-400">
          <Star className="h-8 w-8 mx-auto mb-2 text-slate-600" />
          <p>No reviews yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reviews.map((review) => (
            <ReviewCard key={review.id} review={review} />
          ))}
        </div>
      )}
    </div>
  );
}

// Compact review prompt for completed requests
export function ReviewPrompt({ request, onReviewClick }) {
  if (request?.status !== 'completed' || !request?.provider_id) {
    return null;
  }
  
  return (
    <Card className="border-amber-500/30 bg-gradient-to-r from-amber-500/5 to-orange-500/5" data-testid="review-prompt">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-amber-500/10">
              <Star className="h-5 w-5 text-amber-400" />
            </div>
            <div>
              <p className="font-medium">How was your service?</p>
              <p className="text-sm text-slate-400">Rate your experience with {request.provider_name}</p>
            </div>
          </div>
          <Button 
            onClick={onReviewClick}
            className="bg-amber-500 hover:bg-amber-400 text-black"
            data-testid="leave-review-btn"
          >
            Leave Review
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export { StarRating };
export default ReviewDialog;
